import OmniTable from "../../omni-common/components/OmniTable";
import OmniCheckbox from "../../omni-common/components/OmniCheckbox";
import { formatDate } from "../../../utilities/format";
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { PostStatus } from "../../../lib/apollo/graphql/omni/omni-generated";

export interface Post {
  postId: string;
  title: string;
  status: PostStatus | "EDITING" | "READY" | "PUBLISHED" | "SCHEDULED" | "PENDING";
  channelId: string;
  channel?: string;
  draftId: string;
  planId: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  publishedAt?: string;
  readyMarkedAt?: string;
  content?: string;
  memo?: string;
}

interface PostTableProps {
  posts: Post[];
  selectedIds: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectPost: (id: string, selected: boolean) => void;
  onPostClick?: (post: Post) => void;
  isLoading?: boolean;
  sortKey?: string;
  sortDirection?: "ASC" | "DESC";
  onSort?: (key: string, direction: "ASC" | "DESC") => void;
}

const STATUS_LABELS: Record<string, string> = {
  EDITING: "검토중",
  READY: "게시예정",
  SCHEDULED: "게시예정",
  PENDING: "보류",
  PUBLISHED: "게시완료",
  DELETED: "삭제됨",
};

const STATUS_COLORS: Record<string, string> = {
  EDITING: "bg-orange-100 text-orange-800 border border-orange-200",
  READY: "bg-blue-100 text-blue-800 border border-blue-200",
  SCHEDULED: "bg-blue-100 text-blue-800 border border-blue-200",
  PENDING: "bg-gray-100 text-gray-800 border border-gray-200",
  PUBLISHED: "bg-green-100 text-green-800 border border-green-200",
  DELETED: "bg-red-100 text-red-800 border border-red-200",
};


export default function PostTable({
  posts,
  selectedIds,
  onSelectAll,
  onSelectPost,
  onPostClick,
  isLoading,
  sortKey,
  sortDirection,
  onSort,
}: PostTableProps) {
  // Debug: 게시글 데이터 확인
  useEffect(() => {
  }, [posts]);

  const isAllSelected = posts.length > 0 && selectedIds.length === posts.length;

  const handleSort = (key: string) => {
    if (!onSort) return;

    // 첫 클릭: ASC, 두 번째 클릭: DESC, 세 번째 클릭: 정렬 초기화
    if (sortKey === key) {
      if (sortDirection === "ASC") {
        onSort(key, "DESC");
      } else {
        // 정렬 초기화 (빈 문자열로 전달)
        onSort("", "ASC");
      }
    } else {
      onSort(key, "ASC");
    }
  };

  const SortableHeader = ({ 
    sortKey: key, 
    children, 
    className,
    style 
  }: { 
    sortKey?: string; 
    children: React.ReactNode; 
    className?: string;
    style?: React.CSSProperties;
  }) => {
    const isSortable = key && onSort;
    const isActive = sortKey === key;
    
    if (!isSortable) {
      return <div className={className} style={style}>{children}</div>;
    }

    const getSortIcon = () => {
      if (isActive) {
        return sortDirection === "ASC" ? (
          <ChevronUpIcon className="h-4 w-4 ml-1" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        );
      }
      return <ChevronUpDownIcon className="h-4 w-4 ml-1" />;
    };

    return (
      <div 
        className={`${className} cursor-pointer transition-colors hover:bg-gray-800 ${
          isActive ? 'bg-gray-800' : ''
        }`}
        style={style}
        onClick={() => handleSort(key)}
      >
        {children}
        {getSortIcon()}
      </div>
    );
  };

  const ChannelColumn = ({
    channelName,
    style
  }: {
    channelName?: string;
    style?: React.CSSProperties;
  }) => {
    return (
      <div
        className="flex items-center justify-center border-e px-2 text-sm"
        style={style}
      >
        <span className="truncate" title={channelName}>
          {channelName || '-'}
        </span>
      </div>
    );
  };

  return (
    <div className="relative h-full flex flex-col">
      <OmniTable.Wrapper className="flex-1">
      <div className="flex w-full items-center rounded-t-lg border border-black bg-black py-2">
        <div className="flex items-center justify-center px-2 text-sm font-bold text-white" style={{ width: '63px', minWidth: '63px' }}>
          <OmniCheckbox
            isChecked={isAllSelected}
            onCheck={onSelectAll}
          />
        </div>
        <div
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '90px', minWidth: '90px' }}
        >
          상태
        </div>
        <div
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '120px', minWidth: '120px' }}
        >
          채널
        </div>
        <SortableHeader
          sortKey="title"
          className="flex items-center justify-center px-2 text-sm font-bold text-white flex-1"
          style={{ minWidth: '200px' }}
        >
          제목
        </SortableHeader>
        <SortableHeader 
          sortKey="createdAt"
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '105px', minWidth: '105px' }}
        >
          생성일자
        </SortableHeader>
        <SortableHeader 
          sortKey="updatedAt"
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '105px', minWidth: '105px' }}
        >
          수정일자
        </SortableHeader>
      </div>
      
      <OmniTable.Body isLoading={isLoading} isEmpty={!posts?.length}>
        {posts?.map((post) => {
          const isSelected = selectedIds.includes(post.postId);

          return (
            <div
              key={post.postId}
              className="relative"
              data-post-id={post.postId}
            >
              <OmniTable.Row
                onClick={() => onPostClick?.(post)}
              >
              <div 
                className="flex items-center justify-center border-e px-2"
                style={{ width: '63px', minWidth: '63px' }}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <OmniCheckbox
                    isChecked={isSelected}
                    onCheck={(checked: boolean) => onSelectPost(post.postId, checked)}
                  />
                </div>
              </div>
              
              <div 
                className="flex items-center justify-center border-e px-2"
                style={{ width: '90px', minWidth: '90px' }}
              >
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    STATUS_COLORS[post.status]
                  }`}
                >
                  {STATUS_LABELS[post.status]}
                </span>
              </div>
              
              <ChannelColumn
                channelName={post.channel}
                style={{ width: '120px', minWidth: '120px' }}
              />
              
              <div 
                className="flex items-center justify-start border-e px-2 flex-1"
                style={{ minWidth: '200px' }}
              >
                <span className="truncate" title={post.title}>
                  {post.title}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-center border-e px-2 text-sm"
                style={{ width: '105px', minWidth: '105px' }}
              >
                {formatDate(post.createdAt, "YY.MM.DD")}
              </div>
              
              <div 
                className="flex items-center justify-center px-2 text-sm"
                style={{ width: '105px', minWidth: '105px' }}
              >
                {formatDate(post.updatedAt, "YY.MM.DD")}
              </div>
            </OmniTable.Row>
          </div>
          );
        })}
      </OmniTable.Body>
      </OmniTable.Wrapper>
    </div>
  );
}